import { PhaserGeneralGameObjectType, TiledGameObject } from "src/games/shared/phaserHelpers/HandyTypes";
import TranslateApi from "src/utils/TranslateHelper";
import { Color } from "src/games/shared/ColorValue";
import initializeInteractiveCursor from "src/games/shared/InteractiveCursorUtil";
import { PhaserButtonConfig, PhaserButton } from "src/games/shared/Button";
import { PhaserModalConfig, PhaserModal } from "src/games/shared/Modal";
import { Item } from "../objects/Item";
import { EndGameScene } from "src/games/WarehouseGame/scenes/EndGameScene";

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    background: Phaser.GameObjects.Image;
    map: Phaser.Tilemaps.Tilemap;
    items: Item[];
    crateZone: Phaser.GameObjects.Zone;
    crate: Phaser.GameObjects.Image;
    debug: boolean = false; // Change to true if you want to see the polygons drawn
    shapes;
    hintButton: PhaserButton;
    doneButton: PhaserButton;

    private translateString = "Warehouse.Phaser.MainScene.";

    constructor() {
        super({
            key: "MainScene" + Math.random()
        });
    }

    preload(): void {
        this.load.tilemapTiledJSON('map', require("/src/assets/qci/maps/warehouse_game.json"));
        this.load.atlas("assets", require("/src/assets/qci/atlas/gameWarehouse.png"), require("/src/assets/qci/atlas/gameWarehouse.json"));
        this.load.json("shapes", require("/src/assets/qci/atlas/gameWarehousePhysics.json"));
        this.load.image("delete", require('/src/assets/qci/sprites/delete_icon.png'));
        this.load.image('background', require("/src/assets/qci/backgrounds/planet_c_top.jpg"));
    }

    create(): void {
        this.initialize();
        this.generateScene();
        this.generateEvents();
        initializeInteractiveCursor(this);
    }

    initialize() {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: 'map' });
        this.items = [];
        this.shapes = this.cache.json.get("shapes");
    }

    generateScene() {
        this.background = this.add.image(0, 0, "background");
        this.background.setOrigin(0);
        this.background.setDisplaySize(this.gameWidth, this.gameHeight);

        this.addObjects();
        this.addItems();
    }

    generateEvents() {
        // Wire up events
        this.input.on('dragstart', (pointer: Phaser.Input.Pointer, gameObject: PhaserGeneralGameObjectType) => {
            var item = this.getItemFromGameObject(gameObject);
            this.children.bringToTop(item.object);
            item.object.setDisplaySize(item.placedWidth, item.placedHeight);
            this.setCurrentPolygon(item);
        });

        this.input.on('drag', (pointer: Phaser.Input.Pointer, gameObject: PhaserGeneralGameObjectType, dragX: number, dragY: number) => {
            var item = this.getItemFromGameObject(gameObject);
            item.object.x = pointer.x - item.object.displayWidth / 2;
            item.object.y = pointer.y - item.object.displayHeight / 2;
        });

        this.input.on('dragend', (pointer: Phaser.Input.Pointer, gameObject: PhaserGeneralGameObjectType, dropped: boolean) => {
            var item = this.getItemFromGameObject(gameObject);
            this.setCurrentPolygon(item);
            if (this.debug) {
                this.drawPolygon(item.currentPolygon);
            }
            this.checkItem(gameObject);
        });
    }

    addObjects(): void {
        var layer = this.map.getObjectLayer('Objects');
        var config = new PhaserButtonConfig();
        config.smartSize = true;
        var buttonText, button;
        layer.objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "crate") {
                this.crate = this.add.image(ob.x, ob.y, "assets", ob.name);
                this.crate.setOrigin(0);
                this.crate.setDisplaySize(ob.width, ob.height);
            }
            else if (ob.name === "crateZone") {
                this.crateZone = this.add.zone(ob.x, ob.y, ob.width, ob.height);
                this.crateZone.setOrigin(0);
                this.crateZone.setInteractive();
            }
            else if (ob.name === "items") {
                var side = this.add.image(ob.x, ob.y, "pixel");
                side.setOrigin(0);
                side.setDisplaySize(ob.width, ob.height);
                side.setTint(Color.KUDER_SECONDARY);

                var textStyle = { fontFamily: 'Russo One, Arial', color: "#ffffff", fontSize: 20, padding: 10 };
                var text = this.add.text(0, 20, TranslateApi.translate(this.translateString + "moveItems"), textStyle);
                text.setWordWrapWidth(300, true);
                text.setX(side.x + side.displayWidth / 2 - text.width / 2);
            }
            else if (ob.name === "done") {
                buttonText = TranslateApi.translate(this.translateString + "buttonText");
                config.backgroundColor = Color.KUDER_PRIMARY_BUTTON;
                this.doneButton = new PhaserButton(this, ob.x, ob.y, ob.width, ob.height, buttonText, config);
                this.doneButton.on('buttonClicked', () => {
                    if (this.doneButton.isEnabled()) {
                        this.enableEverything(false);
                        this.tryToEndGame();
                    }
                });
            }
            else if (ob.name === "hint") {
                buttonText = TranslateApi.translate(this.translateString + "hint");
                config.backgroundColor = Color.KUDER_SECONDARY_BUTTON;
                this.hintButton = new PhaserButton(this, ob.x, ob.y, ob.width, ob.height, buttonText, config);
                this.hintButton.on('buttonClicked', () => {
                    if (this.hintButton.isEnabled()) {
                        this.enableEverything(false);
                        this.giveHint();
                    }
                });
            }
            else if (ob.name === "pilot") {
                var pilot = this.add.image(ob.x, ob.y, "pilots", "c");
                pilot.setOrigin(0);
                pilot.setDisplaySize(ob.width, ob.height);
            }
        });
    }

    addItems(): void {
        var layer = this.map.getObjectLayer("Items");
        layer.objects.forEach((ob: TiledGameObject) => {
            var object = this.add.image(ob.x, ob.y, "assets", ob.name);
            object.setOrigin(0);
            object.setDisplaySize(ob.width, ob.height);
            object.setInteractive(this.shapes[ob.name]);
            object.setName(ob.name);

            var bounds = object.getBounds();
            var background = this.add.image(bounds.x, bounds.y, "pixel");
            background.setOrigin(0);
            background.setTint(0x000000);
            background.setAlpha(0.3);
            background.setDisplaySize(bounds.width, bounds.height);
            background.setVisible(false);
            this.children.moveDown(background);

            this.input.setDraggable(object);

            var item = new Item(ob.name, ob.x, ob.y, ob.properties["placedWidth"], ob.properties["placedHeight"]);
            item.object = object;
            item.startHeight = ob.height;
            item.startWidth = ob.width;
            var polyPoints: Phaser.Geom.Point[] = [];
            for (var i = 0; i < this.shapes[ob.name].fixtures[0].vertices.length; i++) {
                this.shapes[ob.name].fixtures[0].vertices[i].forEach((v) => {
                    polyPoints.push(new Phaser.Geom.Point(v.x, v.y));
                });
            }

            var sorted = this.sortPoints(polyPoints);

            item.polygon = new Phaser.Geom.Polygon(sorted);
            this.items.push(item);
        });
    }

    drawPolygon(polygon: Phaser.Geom.Polygon): void {
        var graphics = this.add.graphics();
        graphics.lineStyle(2, 0x000000);
        graphics.beginPath();
        graphics.moveTo(polygon.points[0].x, polygon.points[0].y);
        for (var j = 1; j < polygon.points.length; j++) {
            graphics.lineTo(polygon.points[j].x, polygon.points[j].y);
        }
        graphics.closePath();
        graphics.strokePath();
    }

    checkItem(gameObject: PhaserGeneralGameObjectType): void {
        var item = this.getItemFromGameObject(gameObject);
        if (!this.isItemInCrateZone(item)) {
            item.successfullyPlaced = false;
            this.putItemBack(item);
            return;
        }
        if (this.isItemOverlappingWithOtherItems(item)) {
            item.successfullyPlaced = false;
            this.putItemBack(item);
            return;
        }

        // Mark this item as successfully placed
        item.successfullyPlaced = true;
    }

    setCurrentPolygon(item: Item): void {
        var newPolyPoints = [];
        item.polygon.points.forEach((p) => {
            let x = item.object.x + p.x * item.object.scaleX;
            let y = item.object.y + p.y * item.object.scaleY;
            newPolyPoints.push(new Phaser.Geom.Point(x, y));
        });
        item.currentPolygon = new Phaser.Geom.Polygon(newPolyPoints);
    }

    putItemBack(item: Item): void {
        item.object.setPosition(item.startX, item.startY);
        item.object.setDisplaySize(item.startWidth, item.startHeight);
    }

    isItemInCrateZone(item: Item): boolean {
        var union = Phaser.Geom.Rectangle.Union(this.crateZone.getBounds(), item.object.getBounds());
        if (Phaser.Geom.Rectangle.Perimeter(union) - Phaser.Geom.Rectangle.Perimeter(this.crateZone.getBounds()) <= 3) {
            return true;
        }
        else {
            // If they were close to the crate, shake the crate
            if (Phaser.Geom.Intersects.RectangleToRectangle(this.crate.getBounds(), item.object.getBounds())) {
                this.shakeObject(this.crate);
            }
            return false;
        }
    }

    shakeObject(target: Phaser.GameObjects.Image): void {
        this.add.tween({
            targets: target,
            x: target.x + 5,
            ease: "Bounce",
            duration: 50,
            repeat: 3,
            yoyo: true,
        });
    }

    isItemOverlappingWithOtherItems(itemToCheck: Item): boolean {
        var item: Item;
        for (item of this.items) {
            if (this.doPolygonsOverlap(itemToCheck, item) && Phaser.Geom.Intersects.RectangleToRectangle(this.crate.getBounds(), item.object.getBounds())) {
                this.shakeObject(item.object);
                return true;
            }
        }

        return false;
    }

    doPolygonsOverlap(item1: Item, item2: Item): boolean {
        var poly1 = item1.currentPolygon;
        var poly2 = item2.currentPolygon;

        if (poly1 == null || poly2 == null || item1.name === item2.name) {
            return false;
        }

        var p: Phaser.Geom.Point;
        for (p of poly1.points) {
            if (poly2.contains(p.x, p.y)) {
                return true;
            }
        }
        return false;
    }

    isGameOver(): boolean {
        var item: Item;
        for (item of this.items) {
            if (!item.successfullyPlaced) {
                return false;
            }
        }
        return true;
    }

    /**
     * Show one of a few different images of the crate correctly packed
     */
    giveHint(): void {
        var imageBackground = this.add.image(0, 0, "pixel");
        imageBackground.setOrigin(0);
        imageBackground.setTint(0x000000);
        imageBackground.setDisplaySize(this.crate.displayWidth + 20, this.crate.displayHeight + 20);
        imageBackground.setPosition(this.crate.x - 10, this.crate.y - 10);

        var hintImage = this.add.image(0, 0, "assets", "solution" + Phaser.Math.Between(1, 3));
        hintImage.setOrigin(0);
        hintImage.setDisplaySize(this.crate.displayWidth, this.crate.displayHeight);
        hintImage.setPosition(this.crate.x, this.crate.y);

        var textStyle = { fontFamily: 'Russo One, Arial', color: "#ffffff", fontSize: 20, padding: 10 };
        var text = this.add.text(0, imageBackground.getTopLeft().y + 10, TranslateApi.translate(this.translateString + "hintText"), textStyle);
        text.setX(imageBackground.x + imageBackground.displayWidth / 2 - text.width / 2);

        var exitButton = this.add.image(0, 0, "delete");
        exitButton.setOrigin(0);
        exitButton.setDisplaySize(50, 50);
        exitButton.setInteractive({ useHandCursor: true });
        exitButton.setPosition(imageBackground.getTopRight().x + exitButton.displayWidth - 10, imageBackground.y);

        this.enableEverything(false);

        exitButton.on('pointerdown', () => {
            hintImage.destroy();
            text.destroy();
            imageBackground.destroy();
            exitButton.destroy();
            this.enableEverything(true);
        });
    }

    enableEverything(enabled: boolean): void {
        this.doneButton.setEnabled(enabled);
        this.hintButton.setEnabled(enabled);
        this.items.forEach((i: Item) => {
            i.object.input.enabled = enabled;
        });
    }

    tryToEndGame(): void {
        var config = new PhaserModalConfig();
        config.buttonWidth = 100;
        var modal, options;

        if (this.isGameOver()) {
            this.input.setDefaultCursor("default");
            this.input.removeAllListeners();
            this.scene.add("EndGameScene", EndGameScene, true);
            this.scene.remove(this);
        }
        else {
            var buttonConfig = new PhaserButtonConfig();
            buttonConfig.smartSize = true;
            config.buttonConfig = buttonConfig;
            config.title = TranslateApi.translate(this.translateString + "notDoneText");
            options = [{ displayText: TranslateApi.translate(this.translateString + "ok"), id: 1 }];
            config.buttonWidth = 100;
            modal = new PhaserModal(this, 0, 0, options, config);
            modal.setPosition(this.gameWidth / 2 - modal.width / 2, this.gameHeight / 2 - modal.height / 2);
            modal.on('optionClicked', (op) => {
                this.enableEverything(true);
                modal.destroy();
            });
        }
    }

    getItemFromGameObject(gameObject: PhaserGeneralGameObjectType): Item {
        var item = this.items.find((i: Item) => {
            return i.name === gameObject.name;
        });
        return item;
    }

    /**
     * Sorts the points in counter-clockwise 
     * Source: https://stackoverflow.com/questions/45660743/sort-points-in-counter-clockwise-in-javascript
     */
    sortPoints(points): Phaser.Geom.Point[] {
        points.sort((a, b) => a.y - b.y);
        // Get center y
        const cy = (points[0].y + points[points.length - 1].y) / 2;
        // Sort from right to left
        points.sort((a, b) => b.x - a.x);
        // Get center x
        const cx = (points[0].x + points[points.length - 1].x) / 2;
        // Center point
        const center = { x: cx, y: cy };

        // Starting angle used to reference other angles
        var startAng;
        points.forEach(point => {
            var ang = Math.atan2(point.y - center.y, point.x - center.x);
            if (!startAng) {
                startAng = ang;
            }
            else {
                if (ang < startAng) {  // ensure that all points are clockwise of the start point
                    ang += Math.PI * 2;
                }
            }
            point.angle = ang; // add the angle to the point
        });

        // Sort clockwise;
        points.sort((a, b) => a.angle - b.angle);
        return points;
    }
}



// WEBPACK FOOTER //
// ./src/games/WarehouseGame/scenes/MainScene.ts